import { useEffect, useId, useRef, useState } from 'react'
import type {
  RechazarBody,
  SolicitudCambioListItem,
} from '../services/jefeCocina'
import { ModalShell } from './ui/ModalShell'

interface RechazarF11ModalProps {
  solicitud: SolicitudCambioListItem
  onConfirm: (body: RechazarBody) => Promise<void> | void
  onCancel: () => void
}

const MIN_LEN = 10

export default function RechazarF11Modal({
  solicitud,
  onConfirm,
  onCancel,
}: RechazarF11ModalProps) {
  const [motivo, setMotivo] = useState('')
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const helpId = useId()

  useEffect(() => {
    dialogRef.current?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel, submitting])

  const trimmed = motivo.trim()
  const clientError = trimmed.length < MIN_LEN
    ? `Motivo debe tener al menos ${MIN_LEN} caracteres (actual: ${trimmed.length}).`
    : null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (clientError) return
    setServerError(null)
    setSubmitting(true)
    try {
      await onConfirm({ motivo_chef: trimmed })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al rechazar'
      setServerError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell
      title="Rechazar cambio de dieta"
      subtitle={<>Hab. {solicitud.habitacion} — {solicitud.paciente_nombre}</>}
      onClose={onCancel}
      closeDisabled={submitting}
      maxWidth={480}
    >
      <div ref={dialogRef} tabIndex={-1}>
        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <label className="block">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              Motivo del rechazo
            </span>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              onBlur={() => setTouched(true)}
              maxLength={2000}
              rows={4}
              aria-describedby={helpId}
              aria-invalid={touched && clientError ? true : undefined}
              required
              className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50"
              placeholder="Explicale a la enfermera por que no procede..."
            />
            <span id={helpId} className="text-xs text-gray-600 dark:text-gray-400">
              Minimo {MIN_LEN} caracteres. Actual: {trimmed.length}.
            </span>
          </label>

          {touched && clientError && (
            <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {clientError}
            </p>
          )}
          {serverError && (
            <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {serverError}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 border border-gray-300 dark:border-gray-600 py-2 rounded text-sm disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || (touched && !!clientError)}
              className="flex-1 bg-avante-danger text-white py-2 rounded text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? 'Rechazando…' : 'Confirmar rechazo'}
            </button>
          </div>
        </form>
      </div>
    </ModalShell>
  )
}
